/* eslint-disable */
import { of } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, map, retry } from 'rxjs/operators';
import envGet from '../../Support/Env/Get/EnvGet.support';
import { buildUrl } from '../../Support/Url';
import './HereMaps.service.i18n';

const baseUrl = envGet({ key: `REACT_APP_BORAL_BASE_URL` });
const hereMapsApiKey = envGet({ key: `REACT_APP_BORAL_HERE_MAPS_API_KEY` });
const hereMapsAutocompleteUrl = envGet({ key: `REACT_APP_BORAL_HERE_MAPS_AUTOCOMPLETE_URL` });
const hereMapsGeocoderUrl = envGet({ key: `REACT_APP_BORAL_HERE_MAPS_GEOCODER_URL` });
const serviceablePlantsUrl = envGet({ key: `REACT_APP_BORAL_SERVICEABLE_PLANTS_URL` });

/**
 * HereMapsService
 * @class HereMapsService
 */
class HereMapsService {
  /**
   * The `AuthService`
   * @type {AuthService}
   * @memberof OrderService
   */
  authService;

  /**
   * Creates an instance of `HereMapsService`
   * @param {HereMapsServiceCreateDto} dto The params required to create an instance of `HereMapsService`
   * @memberof HereMapsService
   */
  constructor({ platform, authService }) {
    this.platform = platform;
    this.authService = authService;
  }

  /**
   * Creates custom default layers using Raster Tile API v3
   * @returns {Object} Custom layers object
   * @memberof HereMapsService
   */
  createCustomDefaultLayers = () => {
    try {
      // Use the platform's built-in layer creation but with v3 configuration
      // First try to get default layers from platform
      const defaultLayers = this.platform.createDefaultLayers({
        // Override with v3 tile service configuration
        tileSize: 512,
        pois: true
      });

      // If default layers work, return them
      if (defaultLayers && defaultLayers.vector && defaultLayers.vector.normal) {
        return defaultLayers;
      }

      // Fallback: create custom layers with v3 API URLs
      const baseMapUrl = 'https://maps.ls.hereapi.com/maptile/2.1/maptile/newest/normal.day';

      const normalDayLayer = new window.H.map.layer.TileLayer(
        new window.H.map.provider.ImageTileProvider({
          uri: `${baseMapUrl}/{z}/{x}/{y}/512/png8?apikey=${hereMapsApiKey}`,
          max: 20,
          min: 0
        })
      );

      return {
        raster: {
          normal: {
            map: normalDayLayer,
            base: normalDayLayer,
            xbase: normalDayLayer
          }
        },
        vector: {
          normal: {
            map: normalDayLayer,
            base: normalDayLayer,
            xbase: normalDayLayer
          }
        }
      };
    } catch (error) {
      console.error('Error creating custom layers:', error);
      // Ultimate fallback - use original platform method
      return this.platform.createDefaultLayers();
    }
  };

  /**
   * Renders HereMap
   * @param {Object} nodeRef Node reference
   * @returns {void} void
   * @memberof HereMapsService
   */
  renderMap = async ({ nodeRef }) => {
    try {
      // Obtain the default map types from the platform object:
      const defaultLayers = this.platform.createDefaultLayers();

      // Instantiate (and display) a map object:
      const hereMap = new window.H.Map(nodeRef, defaultLayers.vector.normal.map, {
        zoom: 10,
        center: { lat: 52.5, lng: 13.4 },
      });
      return hereMap;
    } catch (error) {
      console.error('Error rendering HERE Map:', error);
      throw error;
    }
  };

  /**
   * Gets address suggestions for given keyword
   * @param {string} keywords Keywords
   * @returns {Promise} The found `user`
   * @memberof HereMapsService
   */
  getAddressSuggestions = ({ keywords }) => {
    const built = {
      apiKey: hereMapsApiKey,
      q: keywords,
      in: `countryCode:AUS`,
      lang: `EN`

    };

    const url = buildUrl(`${hereMapsAutocompleteUrl}`, built);

    return ajax({
      url,
    }).pipe(
      retry(1),
      map((ajaxObject) => {
        return ajaxObject.response;
      }),
    );
  };

  /**
   * Gets coordinates for locationId
   * @param {string} locationId locationId to find coordinates for
   * @returns {Object} Coordinates
   * @memberof HereMapsService
   */
  getCoordinates = ({ id }) => {
    const built = {
      apiKey: hereMapsApiKey,
      id:id,
    };

    const url = buildUrl(`${hereMapsGeocoderUrl}`, built);

    return ajax({
      url,
    }).pipe(
      map((ajaxObject) => {
        return ajaxObject.response;
      }),
    );
  };

  /**
   * Gets list of serviceable plants. The list is empty if none of the plants are in serviceable area
   * @param {Object} coordinates locationId to find coordinates for
   * @returns {Object} Coordinates
   * @memberof HereMapsService
   */
  getServiceablePlants = ({ lat, lng }) => {
    const headers = this.authService.createHeaders();
    const built = {
      latitude: lat,
      longitude: lng
    };
    const url = buildUrl(`${baseUrl}${serviceablePlantsUrl}`, built);

    return ajax({
      headers,
      url,
    }).pipe(
      map((ajaxObject) => {
        return ajaxObject.response;
      }),
      catchError((ex) => {
        return of();
      }),
    );
  };

  /**
   * render map with marker
   * @param {Object} dto The required params to render HereMap
   * @returns {Object} Object with map and a marker
   * @memberof HereMapsService
   */
  renderMapWithDraggableMarker = ({ nodeRef, H, coords }) => {
    // Use original platform default layers for now to avoid errors
    const defaultLayers = this.platform.createDefaultLayers();

    let marker = null;
    const svgMarkup = `<svg id="room-24px" xmlns="http://www.w3.org/2000/svg" width="77.042" height="77.042" viewBox="0 0 77.042 77.042">
    <path id="Path_2771" data-name="Path 2771" d="M27.471,2A22.454,22.454,0,0,0,5,24.471C5,41.324,27.471,66.2,27.471,66.2S49.941,41.324,49.941,24.471A22.454,22.454,0,0,0,27.471,2Zm0,30.5A8.025,8.025,0,1,1,35.5,24.471,8.028,8.028,0,0,1,27.471,32.5Z" transform="translate(11.05 4.42)" fill="#ffde00" stroke="#00a94f" stroke-width="3"/>
    <path id="Path_2772" data-name="Path 2772" d="M0,0H77.042V77.042H0Z" fill="none"/>
    <circle id="Ellipse_110" data-name="Ellipse 110" cx="9" cy="9" r="9" transform="translate(30 20)" fill="#00a94f"/>
  </svg>
  `;

    const markerIcon = new H.map.Icon(svgMarkup, { size: { w: 48, h: 48 } });

    if (coords) {
      marker = new H.map.Marker(
        { lat: coords.lat, lng: coords.lng },
        {
          // mark the object as volatile for the smooth dragging
          volatility: true,
        },
      );
    } else {
      marker = new H.map.Marker(
        { lat: -33.865143, lng: 151.2099 },
        {
          // mark the object as volatile for the smooth dragging
          volatility: true,
          visibility: false,
        },
      );
    }

    marker.setIcon(markerIcon);

    // initialize a map - this map and center it if necessary
    const interactiveMap = new H.Map(nodeRef, defaultLayers.vector.normal.map, {
      center: coords ? { lat: coords.lat, lng: coords.lng } : { lat: -33.865143, lng: 151.2099 },
      zoom: 15,
      pixelRatio: window.devicePixelRatio || 1,
    });

    // add a resize listener to make sure that the map occupies the whole container
    window.addEventListener(`resize`, () => interactiveMap.getViewPort().resize());

    // make the map interactive
    // MapEvents enables the event system
    // Behavior implements default interactions for pan/zoom (also on mobile touch environments)
    const behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(interactiveMap));

    behavior.disable(H.mapevents.Behavior.DRAGGING);
    behavior.disable(H.mapevents.Behavior.WHEELZOOM);

    // Create the default UI:
    const ui = H.ui.UI.createDefault(interactiveMap, defaultLayers, `en-US`);

    ui.getControl(`zoom`).setVisibility(false);
    ui.getControl(`mapsettings`).setVisibility(false);

    // Ensure that the marker can receive drag events
    // disabled for now as this functionality is currently not required
    // marker.draggable = true;

    interactiveMap.addObject(marker);

    // disable the default draggability of the underlying map
    // and calculate the offset between mouse and target's position
    // when starting to drag a marker object:
    interactiveMap.addEventListener(
      `dragstart`,
      (ev) => {
        const { target } = ev;
        const pointer = ev.currentPointer;
        if (target instanceof H.map.Marker) {
          const targetPosition = interactiveMap.geoToScreen(target.getGeometry());
          target.offset = new H.math.Point(pointer.viewportX - targetPosition.x, pointer.viewportY - targetPosition.y);
          behavior.disable();
        }
      },
      false,
    );

    // re-enable the default draggability of the underlying map
    // when dragging has completed
    interactiveMap.addEventListener(
      `dragend`,
      (ev) => {
        const { target } = ev;
        if (target instanceof H.map.Marker) {
          behavior.enable();
        }
      },
      false,
    );

    // Listen to the drag event and move the position of the marker
    // as necessary
    interactiveMap.addEventListener(
      `drag`,
      (ev) => {
        const { target } = ev;
        const pointer = ev.currentPointer;
        if (target instanceof H.map.Marker) {
          target.setGeometry(interactiveMap.screenToGeo(pointer.viewportX - target.offset.x, pointer.viewportY - target.offset.y));
        }
      },
      false,
    );

    return { map: interactiveMap, marker };
  };

  renderTrucksMap = ({ nodeRef, H, coords, zoomHidden }) => {
    // Use original platform default layers for now to avoid errors
    const defaultLayers = this.platform.createDefaultLayers()
    const map = new H.Map(nodeRef, defaultLayers.vector.normal.map, {
      center: coords ? { lat: coords.lat, lng: coords.lng } : { lat: -33.865143, lng: 151.2099 },
      zoom: 15,
      pixelRatio: window.devicePixelRatio || 1,
    })

    // Behavior implements default interactions for pan/zoom (also on mobile touch environments)
    const behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(map));

    // Create the default UI:
    const ui = H.ui.UI.createDefault(map, defaultLayers, `en-US`);
    if(zoomHidden) {
      ui.getControl(`zoom`).setVisibility(false);
    }
    return map
  }
}

export default HereMapsService;
